"use client";
import { useEffect, useState } from "react";

// Global event system for tap hint state
class TapHintManager {
  private listeners: Set<() => void> = new Set();
  private _hasSeenHint: boolean = false;

  constructor() {
    // Initialize from sessionStorage if available
    if (typeof window !== "undefined") {
      this._hasSeenHint =
        sessionStorage.getItem("decorative-box-tap-hint-seen") === "true";
    }
  }

  get hasSeenHint(): boolean {
    return this._hasSeenHint;
  }

  markHintAsSeen(): void {
    if (!this._hasSeenHint) {
      this._hasSeenHint = true;
      if (typeof window !== "undefined") {
        sessionStorage.setItem("decorative-box-tap-hint-seen", "true");
      }
      // Notify all listeners
      this.listeners.forEach((listener) => {
        listener();
      });
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

// Global instance
const tapHintManager = new TapHintManager();

export function useTapHint() {
  const [hasSeenHint, setHasSeenHint] = useState(tapHintManager.hasSeenHint);

  useEffect(() => {
    // Subscribe to changes
    const unsubscribe = tapHintManager.subscribe(() => {
      setHasSeenHint(tapHintManager.hasSeenHint);
    });

    return unsubscribe;
  }, []);

  const markHintAsSeen = () => {
    tapHintManager.markHintAsSeen();
  };

  return {
    hasSeenHint,
    markHintAsSeen,
  };
}
